import { useState } from 'react';
import type { Sort } from '@/components/table/sort';
import { PAGE_SIZES } from '@/models/listPage';
import { useDebouncedValue } from './useDebouncedValue';

/**
 * Filtres, tri, page et taille de page d'une liste paginée côté API. Changer de filtre, de tri, de taille de page ou de
 * resetKey (la société sélectionnée, des filtres communs à plusieurs listes…) ramène en page 1. search : la recherche,
 * envoyée une fois la frappe terminée (vide si la liste n'a pas de recherche à elle).
 */
export function useListControls<Filters extends object, S extends Sort<string> | undefined = undefined>({
  filters: initialFilters,
  sort: initialSort,
  pageSizes = PAGE_SIZES,
  resetKey,
}: {
  filters: Filters;
  sort?: S;
  pageSizes?: readonly number[];
  resetKey?: unknown;
}) {
  const [filters, setFilters] = useState(initialFilters);
  const [sort, setSort] = useState(initialSort as S);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(pageSizes[0]);
  const search = useDebouncedValue(
    ('search' in filters && typeof filters.search === 'string' ? filters.search : '').trim(),
  );

  const [shownResetKey, setShownResetKey] = useState(resetKey);
  if (shownResetKey !== resetKey) {
    setShownResetKey(resetKey);
    setPage(1);
  }

  return {
    filters,
    search,
    sort,
    page,
    itemsPerPage,
    changeFilters: (changes: Partial<Filters>) => {
      setFilters((current) => ({ ...current, ...changes }));
      setPage(1);
    },
    changeSort: (next: S) => {
      setSort(next);
      setPage(1);
    },
    /** Props de <Pagination> (hors totalItems et label) */
    pagination: {
      page,
      itemsPerPage,
      pageSizes,
      onPageChange: setPage,
      onItemsPerPageChange: (size: number) => {
        setItemsPerPage(size);
        setPage(1);
      },
    },
  };
}
